import { Icon, SvgIconNames } from '@toggle/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useSubscription } from '~/stores/use-subscription/useSubscription';
import { useReasonerParams } from '~/views/alfred/research/hooks/useReasonerParams';
import {
  ChatMode,
  useChatNavigation,
} from '~/views/alfred/use-chat-navigation/useChatNavigation';

import { TooltipContainer } from '../tooltip-container/TooltipContainer';
import * as S from './ChatModesButtonGroup.styles';

type ChatModesButtonGroupType = {
  chatMode: ChatMode;
  icon: SvgIconNames;
};

export const ChatModesButtonGroup = ({
  compactMode,
}: {
  compactMode?: boolean;
}) => {
  const { t } = useTranslation(['chat']);
  const { mode, setMode } = useChatNavigation();
  const { isBasicUser, isLightUser } = useSubscription(state => ({
    isBasicUser: state.isBasicUser(),
    isLightUser: state.isLightUser(),
  }));
  const { conversationId } = useReasonerParams();

  const isResearchAllowed =
    ((isLightUser && !!conversationId) || !isLightUser) && !isBasicUser;

  const options: ChatModesButtonGroupType[] = [
    ...((isResearchAllowed
      ? [{ chatMode: 'research', icon: 'DeepResearch' }]
      : []) as ChatModesButtonGroupType[]),
    ...((!isResearchAllowed
      ? [{ chatMode: 'quickAnalysis', icon: 'QuickAnalysis' }]
      : []) as ChatModesButtonGroupType[]),
    { chatMode: 'document', icon: 'DocSearch' },
  ];

  const activeIndex = options.findIndex(option => mode === option.chatMode);

  return (
    <S.StyledButtonGroup
      activeIndex={activeIndex}
      labels={options.map((o, idx) => {
        /* i18next-extract-mark-context-next-line ["research", "quickAnalysis", "document"] */
        const label =
          isResearchAllowed && o.chatMode === 'research'
            ? t('chat:askAlfred')
            : t('chat:analysis', { context: o.chatMode });
        return (
          <TooltipContainer
            key={o.chatMode}
            label={label}
            inPortal
            disabled={!compactMode}
          >
            <S.Wrapper
              $active={activeIndex === idx}
              $compactMode={!!compactMode}
            >
              {<Icon size={16} iconName={o.icon} />}
              {!compactMode && label}
            </S.Wrapper>
          </TooltipContainer>
        );
      })}
      onClick={index => {
        setMode(options[index].chatMode);
      }}
    />
  );
};
